import { useMemo } from 'react'
import { useRouter } from 'next/router'
import { usePriceInfo } from '@hooks/usePriceInfo'
import { useSpecialPrice } from '@hooks/product'
import { enhanceOriginalProduct } from '@utils/product/product-info'
import { KK_LANG_IDS, Locale } from '@model/locales'
import { useInitialData } from '@context/initialDataContext'
import { getPriceValueWithCurrency } from '@utils/currency'
import type { Product } from '@model/product'
import type { Currency } from '@model/product/PriceInfo'
import type { BundleGroups } from '@components/profile/Wishlist/Wishlist'
import * as R from 'ramda'
import { isGloballyPricelist } from '@utils/pricelist-utils/app-properties'

interface UseCartItemPriceParams {
  product: Product
  quantity: number
  currency: Currency
  bundleGroups?: BundleGroups
  quantityProductIsFree?: number
}

export const useCartItemPrice = ({
  product,
  quantity,
  currency,
  bundleGroups,
  quantityProductIsFree,
}: UseCartItemPriceParams) => {
  const { locale } = useRouter()

  const {
    initialData: { allPromotions, currencies },
  } = useInitialData()

  const priceInfo = usePriceInfo(product, allPromotions)
  const { topPrice, normalPrice, isClubOnePrice } = priceInfo
  const discountedPrice = normalPrice > topPrice ? topPrice : 0

  const productCopy = useMemo(() => {
    const copy = { ...product }
    if (product.promotionResults) {
      copy.promotionResults = product.promotionResults.map((r) => ({ ...r }))
    }
    return copy
  }, [product])

  const enhancedProduct = useMemo(
    () => enhanceOriginalProduct(productCopy, KK_LANG_IDS[locale as Locale], allPromotions, quantity),
    [productCopy, locale, allPromotions, quantity]
  )

  const { promoPrice, promotionResults } = enhancedProduct

  const promotionResultsSerialized = useMemo(
    () => JSON.stringify(promotionResults?.map((r) => ({ used: r.used, promotionId: r.promotionId })) || []),
    [promotionResults, quantity]
  )

  const convertedPromoPrice = useMemo(() => {
    if (promoPrice) {
      return getPriceValueWithCurrency(promoPrice, currency, currencies)
    }
    return undefined
  }, [promoPrice, currency, currencies])

  const specialPriceValue = useSpecialPrice(product)
  const hasSpecialPrice = specialPriceValue !== null

  const isConditionMet = useMemo(() => {
    if (!promotionResults || promotionResults.length === 0) return false
    return promotionResults[0].used || false
  }, [promotionResults, promotionResultsSerialized])
  const specialPrice = useMemo(() => {
    if (
      (hasSpecialPrice && priceInfo.specialPrice && isConditionMet && convertedPromoPrice) ||
      (!hasSpecialPrice && !priceInfo.specialPrice && isConditionMet && convertedPromoPrice)
    ) {
      return convertedPromoPrice
    } else if (!hasSpecialPrice && !priceInfo.specialPrice && !isConditionMet && convertedPromoPrice) {
      return normalPrice
    } else if (hasSpecialPrice && priceInfo.specialPrice) {
      return priceInfo.specialPrice
    } else {
      return discountedPrice
    }
  }, [
    hasSpecialPrice,
    priceInfo.specialPrice,
    isConditionMet,
    convertedPromoPrice,
    normalPrice,
    discountedPrice,
    promoPrice,
    quantity,
    promotionResultsSerialized,
  ])

  const isBundleApplied = useMemo(() => {
    if (!priceInfo.bundleLabel) return false

    if (
      (priceInfo.bundleLabel.includes('bundle') || priceInfo.bundleLabel.includes('.buy.')) &&
      bundleGroups &&
      priceInfo.bundleLabel in bundleGroups
    ) {
      const sum = R.compose(R.sum, R.map(R.prop('quantity')))(bundleGroups[priceInfo.bundleLabel])
      const numberOfBundles = Math.floor(sum / priceInfo.bundleQuantity)

      if (numberOfBundles >= 1) return true
    }
    return false
  }, [priceInfo.bundleLabel, priceInfo.bundleQuantity, bundleGroups])

  const basePrice = useMemo(() => {
    if (!quantity) return 0
    if (isBundleApplied) {
      return priceInfo.bundlePricePerItem
    }
    return specialPrice || normalPrice
  }, [quantity, isBundleApplied, priceInfo.bundlePricePerItem, specialPrice, normalPrice])

  const rowFullPrice = useMemo(() => {
    const numberOfBundles = Math.floor(quantity / priceInfo.bundleQuantity)
    const leftOverProductPrices = (quantity - numberOfBundles * priceInfo.bundleQuantity) * priceInfo.topPrice
    if (isGloballyPricelist() && priceInfo.bundlePrice && priceInfo.bundleQuantity) {
      return numberOfBundles * priceInfo.bundlePrice + leftOverProductPrices
    }
    if (!priceInfo.bundleQuantity) return basePrice * quantity

    const totalPrice = priceInfo.topPrice * quantity

    if (quantityProductIsFree && quantityProductIsFree > 0) {
      const result = totalPrice - priceInfo.topPrice * quantityProductIsFree
      return result >= 0 ? result : 0
    }

    return totalPrice
  }, [priceInfo.bundleQuantity, priceInfo.topPrice, basePrice, quantity, quantityProductIsFree])

  return {
    priceInfo,
    specialPrice,
    basePrice,
    rowFullPrice,
    isBundleApplied,
    discountedPrice,
    normalPrice,
    topPrice,
    isClubOnePrice,
    isConditionMet,
  }
}
